// calendarService.js - Centralized service for calendar API operations
import { format, parseISO } from 'date-fns';
import { shareEvent } from '../state/calendarSlice';
import store from '../state';

// Use environment variable for API URL or fallback to localhost
const API_URL = process.env.REACT_APP_CALENDAR_API_URL || 'http://localhost:5004';

// Add a simple cache for event data
const cache = {
  events: new Map(),
  sharedEvents: new Map(),
  shareRequests: new Map(),
  
  // Max age for cached data in milliseconds
  maxAge: 60000, // 1 minute
  
  // Set cache entry with timestamp
  set: (cacheType, key, data) => {
    if (!cache[cacheType]) return;
    cache[cacheType].set(key, {
      data,
      timestamp: Date.now()
    });
  },
  
  // Get cache entry if still valid
  get: (cacheType, key) => {
    if (!cache[cacheType]) return null;
    const entry = cache[cacheType].get(key);
    if (!entry) return null;
    
    // Check if entry is still valid
    if (Date.now() - entry.timestamp > cache.maxAge) {
      cache[cacheType].delete(key);
      return null;
    }
    
    return entry.data;
  },
  
  // Clear all cache
  clear: () => {
    cache.events.clear();
    cache.sharedEvents.clear();
    cache.shareRequests.clear();
  },
  
  // Clear specific cache
  clearType: (cacheType) => {
    if (cache[cacheType]) {
      cache[cacheType].clear();
    }
  }
};

// Normalize event data to ensure consistent format
const normalizeEvent = (event) => {
  if (!event) return null;
  
  // Clone the event to avoid mutating the original
  const normalizedEvent = { ...event };
  
  // Ensure ID is present and consistent
  if (!normalizedEvent.id && normalizedEvent._id) {
    normalizedEvent.id = normalizedEvent._id;
  }
  
  // Handle date fields - ensure both naming conventions work
  // (startTime/endTime and start/end)
  if (normalizedEvent.startTime && !normalizedEvent.start) {
    normalizedEvent.start = normalizedEvent.startTime;
  } else if (normalizedEvent.start && !normalizedEvent.startTime) {
    normalizedEvent.startTime = normalizedEvent.start;
  }
  
  if (normalizedEvent.endTime && !normalizedEvent.end) {
    normalizedEvent.end = normalizedEvent.endTime;
  } else if (normalizedEvent.end && !normalizedEvent.endTime) {
    normalizedEvent.endTime = normalizedEvent.end;
  }
  
  // Ensure userId is present and consistent with creator id
  if (!normalizedEvent.userId && normalizedEvent.creatorId) {
    normalizedEvent.userId = normalizedEvent.creatorId;
  }
  
  // Ensure sharedWith is an array and maintain any user details
  if (!normalizedEvent.sharedWith) {
    normalizedEvent.sharedWith = [];
  }

  // Preserve owner details if they exist
  if (normalizedEvent.owner) {
    // Make sure it has all expected fields
    if (typeof normalizedEvent.owner === 'object') {
      if (!normalizedEvent.owner.id && normalizedEvent.userId) {
        normalizedEvent.owner.id = normalizedEvent.userId;
      }
      if (!normalizedEvent.owner.name) {
        normalizedEvent.owner.name = 'Event Owner';
      }
    }
  } else if (normalizedEvent.userId) {
    // Create a minimal owner object if only userId exists
    normalizedEvent.owner = {
      id: normalizedEvent.userId,
      name: 'Event Owner'
    };
  }
  
  return normalizedEvent;
};

// Enhanced error handling helper
const handleApiError = (error, fallbackData = null, errorMessage = 'API request failed') => {
  if (error.response) {
    // Server responded with an error status
    console.error(`${errorMessage}: ${error.response.status} - ${error.response.statusText}`);
    console.error('Error details:', error.response.data);
  } else if (error.request) {
    // Request was made but no response
    console.error(`${errorMessage}: No response received`);
  } else {
    // Error setting up the request
    console.error(`${errorMessage}: ${error.message}`);
  }
  
  if (fallbackData !== null) {
    return fallbackData;
  }
  throw error;
};

// Fetch helper with caching support
const fetchWithCache = async (url, options = {}, cacheKey = null, cacheType = 'events') => {
  // Check cache if cacheKey provided
  if (cacheKey) {
    const cachedData = cache.get(cacheType, cacheKey);
    if (cachedData) {
      console.log(`Using cached data for ${url}`);
      return cachedData;
    }
  }
  
  try {
    // Get token from Redux store
    const state = store.getState();
    const token = state.auth?.token;
    
    // Prepare headers with Authorization token
    const headers = {
      'Content-Type': 'application/json',
      'Api-Name': 'calendar-api',
      ...options.headers,
      'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
    };
    
    // Update options with headers
    const updatedOptions = {
      ...options,
      headers
    };
    
    const response = await fetch(url, updatedOptions);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    
    // Cache the result if cacheKey provided
    if (cacheKey) {
      cache.set(cacheType, cacheKey, data);
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
};

// Calendar service with all API methods
const calendarService = {
  // Clear all caches when needed (e.g., on user logout)
  clearCaches: () => {
    cache.clear();
  },
  
  // Health check
  checkHealth: async () => {
    try {
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(`${API_URL}/health-check`, {
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'calendar-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        }
      });
      return response.ok;
    } catch (error) {
      console.error('Error checking calendar API health:', error);
      return false;
    }
  },
  
  // Fetch events for a specific day
  fetchEvents: async (date, userId) => {
    try {
      if (!date || !userId) {
        throw new Error('Date and userId are required for fetching events');
      }
      
      // Format date as ISO string if it's not already
      const formattedDate = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      
      // Create cache key
      const cacheKey = `day_${formattedDate}_${userId}`;
      
      const data = await fetchWithCache(
        `${API_URL}/events/day?date=${formattedDate}&userId=${userId}`,
        { method: 'GET' },
        cacheKey,
        'events'
      );
      
      return data;
    } catch (error) {
      return handleApiError(error, { events: [], totalEvents: 0 }, 'Error in fetchEvents');
    }
  },
  
  // Fetch events for a specific week
  fetchWeekEvents: async (date, userId) => {
    try {
      if (!date || !userId) {
        throw new Error('Date and userId are required for fetching week events');
      }
      
      // Assume date is the start of the week
      const startDate = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
      
      // Calculate end date (1 week from start)
      const endDate = new Date(new Date(startDate).getTime() + 7 * 24 * 60 * 60 * 1000);
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
      
      // Create cache key
      const cacheKey = `week_${startDate}_${formattedEndDate}_${userId}`;
      
      const data = await fetchWithCache(
        `${API_URL}/events/week?startDate=${startDate}&endDate=${formattedEndDate}&userId=${userId}`,
        { method: 'GET' },
        cacheKey,
        'events'
      );
      
      return data;
    } catch (error) {
      return handleApiError(error, { events: [], totalEvents: 0 }, 'Error in fetchWeekEvents');
    }
  },
  
  // Fetch events for a specific month
  fetchMonthEvents: async (date, userId) => {
    try {
      if (!date || !userId) {
        throw new Error('Date and userId are required for fetching month events');
      }
      
      // Extract year and month from date
      const dateObj = new Date(date);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth() + 1; // JavaScript months are 0-indexed
      
      // Create cache key
      const cacheKey = `month_${year}_${month}_${userId}`;
      
      const data = await fetchWithCache(
        `${API_URL}/events/month?year=${year}&month=${month}&userId=${userId}`,
        { method: 'GET' },
        cacheKey,
        'events'
      );
      
      return data;
    } catch (error) {
      return handleApiError(error, { events: [], totalEvents: 0 }, 'Error in fetchMonthEvents');
    }
  },
  
  // Fetch events by date range
  fetchEventsByRange: async (startDate, endDate, userId) => {
    try {
      if (!startDate || !endDate || !userId) {
        throw new Error('Start date, end date, and userId are required for fetching events by range');
      }
      
      // Format dates as ISO strings if they're not already
      const formattedStartDate = typeof startDate === 'string' ? startDate : format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = typeof endDate === 'string' ? endDate : format(endDate, 'yyyy-MM-dd');
      
      // Create cache key
      const cacheKey = `range_${formattedStartDate}_${formattedEndDate}_${userId}`;
      
      const data = await fetchWithCache(
        `${API_URL}/events/range?startDate=${formattedStartDate}&endDate=${formattedEndDate}&userId=${userId}`,
        { method: 'GET' },
        cacheKey,
        'events'
      );
      
      return data;
    } catch (error) {
      return handleApiError(error, { events: [], totalEvents: 0 }, 'Error in fetchEventsByRange');
    }
  },
  
  // Fetch shared events
  fetchSharedEvents: async (userId) => {
    try {
      if (!userId) {
        console.error('User ID is required for fetching shared events');
        return { events: [] };
      }
      
      console.log(`calendarService: Fetching shared events for user ${userId}`);
      
      // Create cache key
      const cacheKey = `shared_${userId}`;
      
      const data = await fetchWithCache(
        `${API_URL}/events/shared?userId=${userId}`,
        { 
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        cacheKey,
        'sharedEvents'
      );
      
      // Mark these events as accepted shared events for UI handling
      const markedEvents = (data.events || []).map(event => ({
        ...event,
        isShared: true,
        shareStatus: 'accepted' // Explicitly mark as accepted
      }));
      
      console.log(`Processed ${markedEvents.length} normalized shared events`);
      return { events: markedEvents };
    } catch (error) {
      return handleApiError(error, { events: [] }, 'Error fetching shared events');
    }
  },
  
  // Fetch share requests for a user
  fetchShareRequests: async (userId) => {
    try {
      if (!userId) {
        console.error('User ID is required for fetching share requests');
        throw new Error('User ID is required for fetching share requests');
      }

      console.log(`calendarService: Fetching share requests for user ${userId}`);
      
      // Create cache key
      const cacheKey = `requests_${userId}`;
      
      // Clear share requests cache on each fetch to ensure fresh data
      cache.clearType('shareRequests');
      
      const data = await fetchWithCache(
        `${API_URL}/events/share-requests?userId=${userId}`,
        { 
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        },
        cacheKey,
        'shareRequests'
      );
      
      console.log(`Share requests fetched from API: ${data.requests ? data.requests.length : 0}`);
      
      // Normalize the share requests
      const normalizedRequests = (data.requests || []).map(request => {
        const normalizedRequest = { ...request };
        
        // Ensure request has a unique ID
        if (!normalizedRequest.id && normalizedRequest._id) {
          normalizedRequest.id = normalizedRequest._id;
        } else if (!normalizedRequest.id && !normalizedRequest._id) {
          normalizedRequest.id = `req_${Math.random().toString(36).substring(2, 10)}`;
        }
        
        // Ensure status is properly set - mandatory for request counting
        if (!normalizedRequest.status && request.status) {
          normalizedRequest.status = request.status;
        } else if (!normalizedRequest.status) {
          normalizedRequest.status = 'pending'; // Default to pending if no status
        }
        
        // Normalize the event data if it exists
        if (normalizedRequest.event) {
          // Mark the event as pending so the UI can handle it appropriately
          normalizedRequest.event = normalizeEvent({
            ...normalizedRequest.event,
            isShared: true,
            shareStatus: 'pending', // Mark as pending for UI filtering
            shareRequestId: normalizedRequest.id // Link to the request
          });
        } 
        // If the event field doesn't exist but we have event data directly on the request
        else if (!normalizedRequest.event && (normalizedRequest.eventId || normalizedRequest.eventTitle)) {
          normalizedRequest.event = normalizeEvent({
            id: normalizedRequest.eventId,
            title: normalizedRequest.eventTitle,
            startTime: normalizedRequest.eventStart,
            endTime: normalizedRequest.eventEnd,
            start: normalizedRequest.eventStart,
            end: normalizedRequest.eventEnd,
            location: normalizedRequest.eventLocation,
            description: normalizedRequest.eventDescription,
            color: normalizedRequest.eventColor,
            isShared: true,
            shareStatus: 'pending', // Mark as pending for UI filtering
            shareRequestId: normalizedRequest.id // Link to the request
          });
        }
        
        // If we still don't have an event object, create an empty one
        if (!normalizedRequest.event) {
          normalizedRequest.event = {
            id: normalizedRequest.eventId || 'unknown',
            title: 'Untitled Event',
            isShared: true,
            shareStatus: 'pending' // Mark as pending for UI filtering
          };
        }
        
        return normalizedRequest;
      });
      
      return { requests: normalizedRequests };
    } catch (error) {
      return handleApiError(error, { requests: [] }, 'Error in fetchShareRequests');
    }
  },
  
  // Create a new event
  createEvent: async (eventData) => {
    try {
      const normalizedData = normalizeEvent(eventData);
      console.log('calendarService: Creating event with normalized data:', normalizedData);
      
      // Make sure required fields exist before sending
      if (!normalizedData.title || !normalizedData.start || !normalizedData.end || !normalizedData.userId) {
        console.error('Missing required fields for event creation:', 
          !normalizedData.title ? 'title ' : '', 
          !normalizedData.start ? 'start ' : '', 
          !normalizedData.end ? 'end ' : '', 
          !normalizedData.userId ? 'userId' : '');
        throw new Error('Missing required fields: title, start, end, or userId');
      }
      
      // Clear caches since we're making a change
      cache.clear();
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(`${API_URL}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'calendar-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify(normalizedData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response from API: ${response.status} - ${errorText}`);
        throw new Error(`Error creating event: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Event created successfully:', data);
      return normalizeEvent(data.event || data);
    } catch (error) {
      return handleApiError(error, null, 'Error in createEvent');
    }
  },
  
  // Update an existing event
  updateEvent: async (eventData) => {
    try {
      console.log('calendarService: Updating event:', eventData);
      
      // Clear caches since we're making a change
      cache.clear();
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(`${API_URL}/events/${eventData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'calendar-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify(eventData),
      });
      
      if (!response.ok) {
        throw new Error(`Error updating event: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return normalizeEvent(data.event);
    } catch (error) {
      return handleApiError(error, null, 'Error in updateEvent');
    }
  },
  
  // Delete an event
  deleteEvent: async (eventId, userId) => {
    try {
      console.log('calendarService: Deleting event:', eventId);
      
      // Clear caches since we're making a change
      cache.clear();
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(`${API_URL}/events/${eventId}?userId=${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'calendar-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error deleting event: ${response.status} ${response.statusText}`);
      }
      
      return { success: true, id: eventId };
    } catch (error) {
      return handleApiError(error, { success: false, id: eventId }, 'Error in deleteEvent');
    }
  },
  
  // Set a reminder for an event
  setReminder: async (eventId, reminderData) => {
    try {
      console.log('calendarService: Setting reminder for event:', eventId);
      
      // Clear caches since we're making a change
      cache.clear();
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(`${API_URL}/events/${eventId}/reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'calendar-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify(reminderData),
      });
      
      if (!response.ok) {
        throw new Error(`Error setting reminder: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return handleApiError(error, null, 'Error in setReminder');
    }
  },
  
  // Respond to a share request
  respondToShareRequest: async (requestId, userId, action) => {
    try {
      if (!requestId || !userId || !action) {
        console.error('Request ID, User ID, and action are required');
        throw new Error('Request ID, User ID, and action are required');
      }

      console.log(`calendarService: Responding to share request ${requestId} with action ${action}`);
      
      // Clear caches since we're making a change
      cache.clear();
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(`${API_URL}/events/share-requests/${requestId}/respond`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'calendar-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        },
        body: JSON.stringify({ userId, action })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Error response from respond API: ${response.status} - ${errorText}`);
        throw new Error(`Failed to respond to share request: ${response.status}`);
      }

      const data = await response.json();
      console.log(`Responded to share request: ${requestId} with ${action}`);
      
      return { ...data, action };
    } catch (error) {
      return handleApiError(error, null, 'Error in respondToShareRequest');
    }
  },
  
  // Share an event with another user
  shareEvent: async (eventId, targetUserId) => {
    try {
      if (!eventId) {
        console.error('Event ID is required for sharing an event');
        throw new Error('Event ID is required for sharing an event');
      }
      
      // Handle both single user ID and arrays of user IDs
      const userIds = Array.isArray(targetUserId) ? targetUserId : [targetUserId];
      
      if (userIds.length === 0) {
        console.error('At least one target user ID is required for sharing an event');
        throw new Error('At least one target user ID is required for sharing an event');
      }
      
      console.log(`calendarService: Sharing event ${eventId} with ${userIds.length} users`);
      
      // Clear caches since we're making a change
      cache.clear();
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      const currentUserId = state.auth?.user?.id;
      
      if (!currentUserId) {
        throw new Error('Current user ID not found in auth state');
      }
      
      // Make API calls for each user
      const results = await Promise.all(userIds.map(async (userId) => {
        try {
          console.log(`Sharing event ${eventId} with user ${userId}`);
          
          const response = await fetch(`${API_URL}/events/${eventId}/share`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Api-Name': 'calendar-api',
              'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
            },
            body: JSON.stringify({ 
              userId: currentUserId,
              targetUserId: userId,
              message: `Shared event with you`
            }),
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error(`Error sharing event with ${userId}: ${response.status} - ${errorText}`);
            return { 
              success: false, 
              userId,
              error: `Failed to share: ${response.status}`
            };
          }
          
          const data = await response.json();
          console.log(`Successfully shared event ${eventId} with user ${userId}:`, data);
          
          // Update local state through Redux action
          store.dispatch(shareEvent({ eventId, userId }));
          
          return { 
            success: true,
            userId,
            requestId: data.requestId
          };
        } catch (error) {
          console.error(`Error sharing event ${eventId} with user ${userId}:`, error);
          return { 
            success: false, 
            userId,
            error: error.message
          };
        }
      }));
      
      const allSucceeded = results.every(result => result.success);
      const successCount = results.filter(result => result.success).length;
      
      return {
        success: successCount > 0,
        allSucceeded,
        successCount,
        totalCount: userIds.length,
        results,
        eventId
      };
    } catch (error) {
      return handleApiError(error, { success: false, eventId }, 'Error in shareEvent');
    }
  }
};

export default calendarService;